import { ConnectError } from '@bufbuild/connect';
import {
  DocumentViewer,
  ReportButton,
  deserify,
  getMediaBasePath,
  useCommonServiceClientContext,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { Feature } from '@protos/school_management/school.db_pb';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../../Auth/Login/login_logout.events';

interface IViewReport {
  homeworkId: bigint;
  studentId: bigint;
  attemptNumber?: number;
  isMultiSubjectAssessment?: boolean;
  isJeeAssessment?: boolean;
  subject?: string;
  className?: string;
  section?: string;
}

export default function ViewStudentReportButton(props: IViewReport) {
  const {
    SchoolAssessmentAPIServiceV2ClientWithStatusCodeHandler,
    SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const user = deserify(useAppSelector((state) => state.auth.userInfo));
  const featureInfo = user?.schoolDetails?.featuresPlanInfo;
  const isViewReportsEnabled = useFeatureEnabled(
    featureInfo,
    Feature.VIEW_REPORTS
  );
  const isSummaryReportsEnabled =
    !props.isMultiSubjectAssessment &&
    !props.isJeeAssessment &&
    useFeatureEnabled(featureInfo, Feature.SUMMARIZED_REPORTS);
  const { setShowAppLoader } = useGlobalContext();
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [isSummaryPdf, setIsSummaryPdf] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  // ----------------------------------------------------

  const onDownloadClick = async (isSummaryReport?: boolean) => {
    try {
      setShowAppLoader(true);
      let reportGen = false;
      let msg = '';
      if (isSummaryReport) {
        const res =
          await SchoolAssessmentAPIServiceV2ClientWithStatusCodeHandler.fetchStudentAssessmentSummaryReport(
            {
              assessmentId: props.homeworkId,
              studentId: props.studentId,
              attemptNumber: props.attemptNumber,
            }
          );
        if (res.data?.gcpSummaryReportUrl) {
          reportGen = true;
          setIsSummaryPdf(true);
          setPdfUrl(getMediaBasePath(res.data?.gcpSummaryReportUrl));
        } else {
          msg = res.message;
        }
      } else {
        const res =
          await SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler.fetchStudentAssessmentReport(
            {
              assessmentId: props.homeworkId,
              studentId: props.studentId,
              attemptNumber: props.attemptNumber,
            }
          );
        if (res.data?.gcpReportUrl) {
          reportGen = true;
          setIsSummaryPdf(false);
          setPdfUrl(getMediaBasePath(res.data?.gcpReportUrl));
        } else {
          msg = res.message;
        }
      }
      if (!reportGen) {
        dispatch(
          setToastInfo({
            label: msg,
            variant: 'success',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    } catch (err) {
      if (err instanceof ConnectError) {
        console.log('Error:', err.rawMessage);

        if (
          err.rawMessage.includes(
            'Unable to generate the assessment report as homework has not been submitted'
          )
        ) {
          dispatch(
            setToastInfo({
              label: 'Assessment has not been submitted.',
              variant: 'error',
              open: true,
            })
          );
        } else {
          dispatch(
            setToastInfo({
              label: err.rawMessage,
              variant: 'error',
              open: true,
            })
          );
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while generating report',
            variant: 'error',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    }
  };

  // ----------------------------------------------------

  const buttonList = [
    {
      name: 'Detailed Report',
      onClick: async () => {
        await onDownloadClick(false);
        await interactionEvent({
          url: '',
          context: 'Your_Assessment',
          name: 'STUDENT_DETAILED_REPORT',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
      },
    },
  ];

  if (isSummaryReportsEnabled) {
    buttonList.push({
      name: 'Summary Report',
      onClick: async () => {
        await onDownloadClick(true);
        await interactionEvent({
          url: '',
          context: 'Your_Assessment',
          name: 'STUDENT_SUMMARY_REPORT',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
      },
    });
  }

  // ----------------------------------------------------

  const classSection = [
    props.className ?? user?.classSectionDetails?.className,
    props.section ?? user?.classSectionDetails?.sectionName,
  ]
    .filter(Boolean)
    .join('_');

  // ----------------------------------------------------

  const reportFileNamePrefix = [props.subject, classSection]
    .filter(Boolean)
    .join('_');

  // ----------------------------------------------------

  if (!isViewReportsEnabled) {
    return null;
  }
  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <ReportButton buttonText="View Report" buttonList={buttonList} />
      </Box>
      {pdfUrl && (
        <DocumentViewer
          downloadUrl={pdfUrl}
          previewUrl={pdfUrl}
          gcpUrl={pdfUrl}
          type={FileEnum.FILE_TYPE_PDF}
          fileName={
            reportFileNamePrefix
              ? `${reportFileNamePrefix}_Geneo_Assessment_${
                  isSummaryPdf ? 'Summary_Report' : 'Report'
                }_${props.homeworkId}_Student_${props.studentId}`
              : `Geneo_Assessment_${
                  isSummaryPdf ? 'Summary_Report' : 'Report'
                }_${props.homeworkId}_Student_${props.studentId}`
          }
          open={!!pdfUrl}
          closeFile={() => {
            setPdfUrl(undefined);
            setIsSummaryPdf(false);
          }}
        />
      )}
    </>
  );
}
